import axios from "axios";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import BarLoader from "react-spinners/BarLoader";
import { setAlert } from "../../../../actions/alert";
import { updateLocation } from "../../../../actions/currentLocation";
import { updateRestaurant } from "../../../../actions/restaurantBuilder";
import { getRestaurantCuisines } from "../../../../util/getRestaurantCuisines";
import CheckUnauth from "../../../auth/CheckUnauth";
import CuisinePicker from "../../../common/CuisinePicker";
import { PanelContainer } from "../../administration/AddGoogleIDs";
import { useJobStatus } from "../../../../hooks/useJobStatus";
import { handleScraperError } from "../../../../utils/errorHandler";
import { slugify } from "../../../../util/slugify";
import {
  getCreationEventContext,
  clearCreationEventContext,
} from "../../../../util/creationEventContext";
import FailureReportModal from "./FailureReportModal";
import useMenuCategorization from "./shared/useMenuCategorization";
import MenuCategorizationPanel from "./shared/MenuCategorizationPanel";
import { getSectionName } from "./shared/menuCategorizationConstants";


const RestaurantCuisine = ({
  setAlert,
  updateRestaurant,
  currRestaurant,
  updateLocation,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    cuisines: [],
    predictedOptions: {
      GUARANTEED_VEGAN: [],
      PROBABLY_VEGAN: [],
      MAYBE_VEGAN: [],
      PROBABLY_NOT_VEGAN: [],
    },
    contributingUrls: [],
  });
  const [loadingVeganOptions, setLoadingVeganOptions] = useState(true);
  const [otherCuisineText, setOtherCuisineText] = useState("");
  const [urlJobId, setUrlJobId] = useState(null);
  const [cuisineError, setCuisineError] = useState("");
  const [failureReportModalOpen, setFailureReportModalOpen] = useState(false);
  const [menuUrlJobId, setMenuUrlJobId] = useState(null);

  // onPredictedOptionsChange callback for the categorization hook.
  // Updates predictedOptions inside formData AND syncs allMenuItems using metadata.
  const handlePredictedOptionsChange = (updater, metadata) => {
    setFormData((prev) => {
      const newPredictedOptions = updater(prev.predictedOptions);
      let newAllMenuItems = prev.allMenuItems;

      if (newAllMenuItems && Array.isArray(newAllMenuItems)) {
        if (metadata?.type === "sectionRename") {
          newAllMenuItems = newAllMenuItems.map((item) => {
            const itemSectionName = getSectionName(item.section);
            if (itemSectionName === metadata.oldName) {
              return {
                ...item,
                section:
                  typeof item.section === "object"
                    ? { ...item.section, name: metadata.newName }
                    : metadata.newName,
              };
            }
            return item;
          });
        } else if (metadata?.type === "itemSectionChange") {
          // Look up target section's existing order from allMenuItems
          let targetOrder = null;
          if (metadata.newSectionName) {
            for (const it of newAllMenuItems) {
              if (getSectionName(it.section) === metadata.newSectionName) {
                const so = typeof it.section === "object" ? it.section.order : null;
                if (so != null) { targetOrder = so; break; }
              }
            }
          }
          newAllMenuItems = newAllMenuItems.map((item) => {
            if (item.option === metadata.optionName) {
              let newSection;
              if (!metadata.newSectionName) {
                newSection = null;
              } else if (typeof item.section === "object") {
                newSection = targetOrder != null
                  ? { name: metadata.newSectionName, order: targetOrder }
                  : { name: metadata.newSectionName };
              } else {
                newSection = metadata.newSectionName;
              }
              return { ...item, section: newSection };
            }
            return item;
          });
        } else if (metadata?.type === "optionChange") {
          newAllMenuItems = newAllMenuItems.map((item) => {
            if (item.option === metadata.optionName) {
              return { ...item, ...metadata.changes };
            }
            return item;
          });
        }
      }

      return {
        ...prev,
        predictedOptions: newPredictedOptions,
        allMenuItems: newAllMenuItems,
      };
    });
  };

  const { cuisines, predictedOptions, googleId } = formData;

  const categorization = useMenuCategorization(
    predictedOptions,
    handlePredictedOptionsChange
  );

  // Build URL index map for source URL footnotes
  const urlIndexMap = {};
  if (formData.contributingUrls) {
    formData.contributingUrls.forEach((url, i) => {
      urlIndexMap[url] = i + 1;
    });
  }
  console.log("DEBUG urlIndexMap:", urlIndexMap, "contributingUrls:", formData.contributingUrls);

  // Use job status hook for menu URL parsing (from failure report)
  const {
    status: menuUrlJobStatus,
    progress: menuUrlJobProgress,
    progressMessage: menuUrlJobProgressMessage,
    result: menuUrlJobResult,
    error: menuUrlJobError,
    timedOut: menuUrlJobTimedOut,
  } = useJobStatus(menuUrlJobId, {
    onStatusUpdate: (data) => {
      // Handle incremental predictedOptions updates
      if (data.result && data.result.predictedOptions) {
        setFormData((currentData) => {
          const currentPredicted = currentData.predictedOptions || {
            GUARANTEED_VEGAN: [],
            PROBABLY_VEGAN: [],
            MAYBE_VEGAN: [],
            PROBABLY_NOT_VEGAN: [],
          };
          const newPredicted = data.result.predictedOptions;

          // Merge incremental updates (combine arrays, avoiding duplicates)
          const mergeOptions = (current, newItems) => {
            const existing = new Set(current.map(item => item.option));
            const merged = [...current];
            for (const item of newItems) {
              if (!existing.has(item.option)) {
                merged.push(item);
                existing.add(item.option);
              }
            }
            return merged;
          };

          return {
            ...currentData,
            predictedOptions: {
              GUARANTEED_VEGAN: mergeOptions(currentPredicted.GUARANTEED_VEGAN || [], newPredicted.GUARANTEED_VEGAN || []),
              PROBABLY_VEGAN: mergeOptions(currentPredicted.PROBABLY_VEGAN || [], newPredicted.PROBABLY_VEGAN || []),
              MAYBE_VEGAN: mergeOptions(currentPredicted.MAYBE_VEGAN || [], newPredicted.MAYBE_VEGAN || []),
              PROBABLY_NOT_VEGAN: mergeOptions(currentPredicted.PROBABLY_NOT_VEGAN || [], newPredicted.PROBABLY_NOT_VEGAN || []),
            },
          };
        });
      }
    },
    onComplete: (result) => {
      if (result && result.predictedOptions) {
        setFormData((currentData) => ({
          ...currentData,
          predictedOptions: result.predictedOptions,
          ...(result.contributingUrls && { contributingUrls: result.contributingUrls }),
          ...(result.predictedOptions.veganLegend && { veganLegend: result.predictedOptions.veganLegend }),
          ...(result.predictedOptions.fullMenuVeganSignal && { fullMenuVeganSignal: result.predictedOptions.fullMenuVeganSignal }),
        }));
      }
      setLoadingVeganOptions(false);
    },
    onError: (error) => {
      console.log("Menu URL parsing error:", error);
      setLoadingVeganOptions(false);
    },
    autoConnect: !!menuUrlJobId,
  });

  // Use job status hook for URL processing
  const {
    status: urlJobStatus,
    progress: urlJobProgress,
    progressMessage: urlJobProgressMessage,
    result: urlJobResult,
    error: urlJobError,
    timedOut: urlJobTimedOut,
  } = useJobStatus(urlJobId, {
    onStatusUpdate: (data) => {
      // Handle incremental predictedOptions updates
      if (data.result && data.result.predictedOptions) {
        setFormData((currentData) => {
          const currentPredicted = currentData.predictedOptions || {
            GUARANTEED_VEGAN: [],
            PROBABLY_VEGAN: [],
            MAYBE_VEGAN: [],
            PROBABLY_NOT_VEGAN: [],
          };
          const newPredicted = data.result.predictedOptions;

          // Merge incremental updates (combine arrays, avoiding duplicates)
          const mergeOptions = (current, newItems) => {
            const existing = new Set(current.map(item => item.option));
            const merged = [...current];
            for (const item of newItems) {
              if (!existing.has(item.option)) {
                merged.push(item);
                existing.add(item.option);
              }
            }
            return merged;
          };

          return {
            ...currentData,
            predictedOptions: {
              GUARANTEED_VEGAN: mergeOptions(currentPredicted.GUARANTEED_VEGAN || [], newPredicted.GUARANTEED_VEGAN || []),
              PROBABLY_VEGAN: mergeOptions(currentPredicted.PROBABLY_VEGAN || [], newPredicted.PROBABLY_VEGAN || []),
              MAYBE_VEGAN: mergeOptions(currentPredicted.MAYBE_VEGAN || [], newPredicted.MAYBE_VEGAN || []),
              PROBABLY_NOT_VEGAN: mergeOptions(currentPredicted.PROBABLY_NOT_VEGAN || [], newPredicted.PROBABLY_NOT_VEGAN || []),
            },
          };
        });
      }

      // Also handle geoJson if present
      if (data.result && data.result.geoJson) {
        setFormData((currentData) => ({ ...currentData, geoJson: data.result.geoJson }));
      }
    },
    onComplete: (result) => {
      console.log("URL onComplete fired, result keys:", result ? Object.keys(result) : "null", "contributingUrls:", result?.contributingUrls);
      if (result) {
        const { predictedOptions, geoJson, contributingUrls } = result;
        const hasItems = predictedOptions &&
          (predictedOptions.GUARANTEED_VEGAN?.length > 0 ||
           predictedOptions.PROBABLY_VEGAN?.length > 0 ||
           predictedOptions.MAYBE_VEGAN?.length > 0 ||
           predictedOptions.PROBABLY_NOT_VEGAN?.length > 0);
        console.log("hasItems:", hasItems, "GUARANTEED:", predictedOptions?.GUARANTEED_VEGAN?.length, "PROBABLY:", predictedOptions?.PROBABLY_VEGAN?.length);
        if (hasItems) {
          console.log("Final predictedOptions:", predictedOptions);
          console.log("contributingUrls from result:", contributingUrls);
          console.log("sample source_url:", predictedOptions.GUARANTEED_VEGAN?.[0]?.source_url || predictedOptions.PROBABLY_VEGAN?.[0]?.source_url || "none");
          setFormData((currentData) => ({
            ...currentData,
            predictedOptions: predictedOptions,
            ...(contributingUrls && { contributingUrls }),
            ...(predictedOptions.veganLegend && { veganLegend: predictedOptions.veganLegend }),
            ...(predictedOptions.fullMenuVeganSignal && { fullMenuVeganSignal: predictedOptions.fullMenuVeganSignal }),
          }));
        } else {
          navigate("/create/v2/cuisine/manual", { state: { ...location.state } });
        }
        if (geoJson) {
          setFormData((currentData) => ({ ...currentData, geoJson }));
        }
      }
      setLoadingVeganOptions(false);
    },
    onError: (error) => {
      console.log("URL processing error:", error);
      setLoadingVeganOptions(false);
      navigate("/create/v2/cuisine/manual", { state: { ...location.state } });
    },
    autoConnect: !!urlJobId,
  });

  useEffect(function syncFormFromLocationState() {
    if (location.state) {
      let { restaurant, fromAdHoc, name, address, geoJson, predictedOptions, status, googleId, website } =
        location.state;

      // Store fromAdHoc flag for later use
      if (fromAdHoc) {
        setFormData((oldData) => ({ ...oldData, fromAdHoc: true }));
      }

      // Ad hoc "open in creator": transform allMenuItems into predictedOptions
      // so the full MenuCategorizationPanel is shown with categorization,
      // report flags, and menu URL attribution.
      if (fromAdHoc && restaurant) {
        setLoadingVeganOptions(false);

        const restaurantAddress = restaurant.address
          ? (typeof restaurant.address === 'string' ? restaurant.address : restaurant.address.formatted_address)
          : address;

        const items = restaurant.allMenuItems?.length > 0
          ? restaurant.allMenuItems
          : (restaurant.veganOptions || []).map(item => ({
              ...item, category: "vegan", originalCategory: "GUARANTEED_VEGAN",
            }));

        const getCategoryBucket = (item) => {
          if (item.originalCategory) return item.originalCategory;
          if (item.category === "vegan") return "GUARANTEED_VEGAN";
          if (item.category === "maybeVegan") return "MAYBE_VEGAN";
          if (item.category === "notVegan") return "PROBABLY_NOT_VEGAN";
          return "MAYBE_VEGAN";
        };
        const mapItem = (item, extra = {}) => {
          const mapped = { option: item.option, description: item.description, probability: item.probability, section: item.section || null, ...extra };
          if (item.source_url) mapped.source_url = item.source_url;
          return mapped;
        };

        predictedOptions = items.length > 0 ? {
          GUARANTEED_VEGAN: items.filter(i => getCategoryBucket(i) === "GUARANTEED_VEGAN").map(i => mapItem(i, { source: i.source })),
          PROBABLY_VEGAN: items.filter(i => getCategoryBucket(i) === "PROBABLY_VEGAN").map(i => mapItem(i)),
          MAYBE_VEGAN: items.filter(i => getCategoryBucket(i) === "MAYBE_VEGAN").map(i => mapItem(i)),
          PROBABLY_NOT_VEGAN: items.filter(i => getCategoryBucket(i) === "PROBABLY_NOT_VEGAN").map(i => mapItem(i)),
        } : predictedOptions;

        name = restaurant.name || name;
        address = restaurantAddress;
        geoJson = restaurant.geoJson || geoJson;
        status = restaurant.status || status || "RESTAURANT";
        googleId = restaurant.googleId || googleId;
        website = restaurant.website || website;
      }

      // Shared flow for both ad hoc categorization and normal scraper results
      {
        const scraperWebsite = website || location.state.website;
        const scraperName = name || location.state.name;
        const scraperAddress = address || location.state.address;
        const scraperGeoJson = geoJson || location.state.geoJson;
        const scraperPredictedOptions = predictedOptions || location.state.predictedOptions;
        const scraperStatus = status || location.state.status || "RESTAURANT";
        const scraperGoogleId = googleId || location.state.googleId;
        const scraperContributingUrls = restaurant?.contributingUrls || location.state.contributingUrls;
        const scraperMenuLinks = location.state.menuLinks;

        setFormData((oldData) => ({
          ...oldData,
          name: scraperName,
          address: scraperAddress,
          geoJson: scraperGeoJson,
          predictedOptions: scraperPredictedOptions,
          status: scraperStatus,
          googleId: scraperGoogleId,
          website: scraperWebsite || null,
          ...(fromAdHoc && { fromAdHoc: true }),
          ...(scraperContributingUrls && { contributingUrls: scraperContributingUrls }),
          ...(scraperMenuLinks && { menuLinks: scraperMenuLinks }),
          ...(scraperPredictedOptions?.veganLegend && { veganLegend: scraperPredictedOptions.veganLegend }),
          ...(scraperPredictedOptions?.fullMenuVeganSignal && { fullMenuVeganSignal: scraperPredictedOptions.fullMenuVeganSignal }),
        }));
      }
    }
  }, [location.state]);

  useEffect(function fetchWebsiteAndProcessMenu() {
    async function getOptions() {
      const { website } = (
        await axios.get(`/api/restaurants/website/${googleId}`)
      ).data;
      if (website) {
        setFormData((oldData) => ({ ...oldData, website }));
        const url = website.replace(/(^\w+:|^)\/\//, "");
        try {
          console.log("Starting async URL processing for:", url);
          const response = await axios.post("/api/restaurant_builder/process_url_async", {
            url: url,
          });
          setUrlJobId(response.data.jobId);
          console.log("URL processing job started:", response.data.jobId);
        } catch (e) {
          console.log(e);
          handleScraperError(e, setAlert);
          setLoadingVeganOptions(false);
          navigate("/create/v2/cuisine/manual", { state: { ...location.state } });
        }
      } else {
        navigate("/create/v2/cuisine/manual", { state: { ...location.state } });
        setLoadingVeganOptions(false);
      }
    }

    // Skip API call if we have predictedOptions (e.g., from ad hoc search)
    const hasPredictedOptions = predictedOptions &&
      (predictedOptions.GUARANTEED_VEGAN?.length > 0 ||
       predictedOptions.PROBABLY_VEGAN?.length > 0 ||
       predictedOptions.MAYBE_VEGAN?.length > 0 ||
       predictedOptions.PROBABLY_NOT_VEGAN?.length > 0);

    if (hasPredictedOptions) {
      setLoadingVeganOptions(false);
    } else if (!predictedOptions && googleId && !urlJobId) {
      setLoadingVeganOptions(true);
      getOptions();
    } else if (!predictedOptions && !googleId) {
      navigate("/create/v2/cuisine/manual", { state: { ...location.state } });
      setLoadingVeganOptions(false);
    }
  }, [googleId, navigate, predictedOptions, location.state, urlJobId]);

  useEffect(function stopLoadingWhenPredictedOptionsArrive() {
    if (
      predictedOptions &&
      (predictedOptions.GUARANTEED_VEGAN?.length > 0 ||
        predictedOptions.PROBABLY_VEGAN?.length > 0 ||
        predictedOptions.MAYBE_VEGAN?.length > 0 ||
        predictedOptions.PROBABLY_NOT_VEGAN?.length > 0)
    ) {
      console.log("staying");
      setLoadingVeganOptions(false);
    }
  }, [predictedOptions]);

  // Safety net: if the job reached a terminal state but we're still loading, stop.
  useEffect(function safetyNetStopLoadingWhenJobDone() {
    if (!loadingVeganOptions) return;
    const jobDone =
      urlJobStatus === "completed" || urlJobStatus === "failed" ||
      menuUrlJobStatus === "completed" || menuUrlJobStatus === "failed";
    if (jobDone) {
      console.log("Safety net: job is done but still loading, forcing stop");
      setLoadingVeganOptions(false);
    }
  }, [loadingVeganOptions, urlJobStatus, menuUrlJobStatus]);

  useEffect(function fetchCuisinesOnMount() {
    async function fetchCuisines() {
      const response = await getRestaurantCuisines();
      setFormData((formData) => ({ ...formData, cuisines: response }));
    }
    fetchCuisines();
  }, []);

  const [selectedCuisines, updateSelectedCuisines] = useState({});

  // Determine if we're in an error/timeout state during loading
  const isMenuUrlFlow = !!menuUrlJobId;
  const hasTimedOut = isMenuUrlFlow ? menuUrlJobTimedOut : urlJobTimedOut;
  const hasError = isMenuUrlFlow ? !!menuUrlJobError : !!urlJobError;
  const showRecovery = loadingVeganOptions && (hasTimedOut || hasError);

  // Retry handler
  const handleRetry = async () => {
    setMenuUrlJobId(null);
    setUrlJobId(null);
    setLoadingVeganOptions(true);

    const website = formData.website || location.state?.website;
    if (!website) {
      setLoadingVeganOptions(false);
      navigate("/create/v2/cuisine/manual", { state: { ...location.state } });
      return;
    }

    const url = website.replace(/(^\w+:|^)\/\//, "");
    try {
      const response = await axios.post("/api/restaurant_builder/process_url_async", {
        url: url,
      });
      setUrlJobId(response.data.jobId);
    } catch (e) {
      console.log(e);
      handleScraperError(e, setAlert);
      setLoadingVeganOptions(false);
      navigate("/create/v2/cuisine/manual", { state: { ...location.state } });
    }
  };

  const constructRestaurant = () => {
    const { predictedOptions, allMenuItems: existingAllMenuItems } = formData;

    let allMenuItems;
    let veganOptions;

    if (existingAllMenuItems && Array.isArray(existingAllMenuItems) && existingAllMenuItems.length > 0) {
      // Ad hoc: preserve original allMenuItems data, only update category
      allMenuItems = existingAllMenuItems.map((item) => ({
        ...item,
        category: categorization.menuItemCategories[item.option] || item.category || "maybeVegan",
      }));
      veganOptions = allMenuItems
        .filter((item) => item.category === "vegan")
        .map((item) => ({
          option: item.option,
          description: item.description,
        }));
    } else {
      // Normal flow: build from categorization hook
      const result = categorization.buildMenuResult();
      allMenuItems = result.allMenuItems;
      veganOptions = result.veganOptions;
    }

    const cuisine = Object.values(
      selectedCuisines.other
        ? {
            ...selectedCuisines,
            other: otherCuisineText.toLowerCase().trim(),
          }
        : selectedCuisines
    );

    return {
      name: formData.name,
      cuisine,
      veganOptions,
      allMenuItems,
      predictedOptions: formData.predictedOptions,
      address: formData.address,
      geoJson: formData.geoJson,
      status: formData.status || "RESTAURANT",
      website: formData.website,
      googleId: formData.googleId,
      ...(formData.contributingUrls?.length > 0 && { contributingUrls: formData.contributingUrls }),
      ...(formData.menuLinks?.length > 0 && { menuLinks: formData.menuLinks }),
    };
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Validate cuisine selection
    const cuisineValues = Object.values(
      selectedCuisines.other
        ? {
            ...selectedCuisines,
            other: otherCuisineText.toLowerCase().trim(),
          }
        : selectedCuisines
    );

    if (cuisineValues.length === 0) {
      setCuisineError("Please select at least one cuisine");
      return;
    }

    setCuisineError("");

    const restaurantData = constructRestaurant();

    // Validate that at least one vegan option is selected
    if (!restaurantData.veganOptions || restaurantData.veganOptions.length === 0) {
      setAlert("Please select at least one vegan option before proceeding", "failure");
      return;
    }

    // If we have geoJson (from Google) but no address (from scraper), skip address page
    if (restaurantData.geoJson && !restaurantData.address) {
      const eventCtx = getCreationEventContext();
      const restaurantToCreate = {
        name: restaurantData.name,
        location: `${restaurantData.geoJson.coordinates[1]},${restaurantData.geoJson.coordinates[0]}`,
        cuisine: restaurantData.cuisine,
        veganOptions: restaurantData.veganOptions,
        allMenuItems: restaurantData.allMenuItems,
        metadata: { isGoogle: false },
        status: restaurantData.status || "RESTAURANT",
        website: restaurantData.website || null,
        googleId: restaurantData.googleId || null,
        ...(restaurantData.address && {
          address: typeof restaurantData.address === 'string'
            ? { formatted_address: restaurantData.address }
            : restaurantData.address
        }),
        ...(eventCtx?.eventId && { eventId: eventCtx.eventId, eventOnly: true }),
      };

      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };

      try {
        const response = await axios.post("/api/restaurants", JSON.stringify(restaurantToCreate), config);
        console.log(response.data);
        setAlert("Restaurant successfully created", "success");
        if (eventCtx?.returnTo) {
          clearCreationEventContext();
          navigate(eventCtx.returnTo);
        } else {
          navigate(`/details/${response.data._id}/${slugify(response.data.name)}`);
        }
        return;
      } catch (err) {
        console.log(err.response);
        setAlert(
          err.response?.data?.errors
            ? err.response.data.errors[0].msg
            : "Error creating restaurant, please try again",
          "failure"
        );
        return;
      }
    }

    navigate("/create/v2/address", { state: restaurantData });
  };

  const createRestaurant = async (restaurant) => {
    const cuisineValues = Object.values(
      selectedCuisines.other
        ? {
            ...selectedCuisines,
            other: otherCuisineText.toLowerCase().trim(),
          }
        : selectedCuisines
    );

    if (cuisineValues.length === 0) {
      setCuisineError("Please select at least one cuisine");
      return;
    }

    setCuisineError("");

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    const eventCtx = getCreationEventContext();
    const body = JSON.stringify({
      ...restaurant,
      ...(eventCtx?.eventId && { eventId: eventCtx.eventId, eventOnly: true }),
    });
    try {
      const response = await axios.post("/api/restaurants", body, config);
      console.log(response.data);
      setAlert("Restaurant successfully created", "success");
      if (eventCtx?.returnTo) {
        clearCreationEventContext();
        navigate(eventCtx.returnTo);
      }
    } catch (err) {
      console.log(err.response);
      setAlert(
        err.response.data.errors
          ? err.response.data.errors[0].msg
          : "Error creating restaurant, please try again",
        "failure"
      );
      console.log(err.response.data.errors);
    }
  };

  const failureReportModalElement = (
    <FailureReportModal
      isOpen={failureReportModalOpen}
      onClose={() => setFailureReportModalOpen(false)}
      restaurantName={formData.name}
      restaurantWebsite={location.state?.website || formData.website || null}
      scrapingMetadata={{
        originalUrl: location.state?.website || formData.website || null,
        predictedOptions: formData.predictedOptions,
        timestamp: new Date(),
      }}
      onReportSubmitted={(result) => {
        if (result.jobId) {
          setMenuUrlJobId(result.jobId);
          setLoadingVeganOptions(true);
        }
      }}
      setAlert={setAlert}
    />
  );

  if (loadingVeganOptions) {
    const activeProgressMessage = isMenuUrlFlow ? menuUrlJobProgressMessage : urlJobProgressMessage;
    const activeProgress = isMenuUrlFlow ? menuUrlJobProgress : urlJobProgress;

    return (
      <div className="create-container">
        {showRecovery ? (
          <div style={{ textAlign: "center", padding: "32px 16px" }}>
            <div style={{ fontSize: "16px", color: "#d32f2f", marginBottom: "16px" }}>
              {hasTimedOut
                ? "Menu analysis seems to be taking too long. The server may not be responding."
                : "Something went wrong while analyzing the menu."}
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: "12px", flexWrap: "wrap" }}>
              <button
                onClick={handleRetry}
                className="btn-primary"
                style={{ padding: "8px 20px", cursor: "pointer" }}
              >
                Retry
              </button>
              <button
                onClick={() => {
                  setLoadingVeganOptions(false);
                  setFailureReportModalOpen(true);
                }}
                style={{
                  padding: "8px 20px",
                  cursor: "pointer",
                  background: "white",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  color: "#424242",
                }}
              >
                Report Issue
              </button>
              <button
                onClick={() => navigate("/create/v2/cuisine/manual", { state: { ...location.state } })}
                style={{
                  padding: "8px 20px",
                  cursor: "pointer",
                  background: "white",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  color: "#424242",
                }}
              >
                Enter Manually
              </button>
            </div>
          </div>
        ) : (
          <>
            <BarLoader
              loading={true}
              color="green"
              height="8px"
              cssOverride={{ margin: "16px" }}
            />
            <div style={{ textAlign: "center", marginTop: "8px", fontSize: "14px" }}>
              {activeProgressMessage || "Analyzing menu..."}{" "}
              {activeProgress > 0 && `(${activeProgress}%)`}
            </div>
          </>
        )}
        {failureReportModalElement}
      </div>
    );
  }

  return (
    <div className="container h-100 ">
      <CheckUnauth {...location} />
      <div className="row create-container">
        <form
          className="form-container"
          onSubmit={handleFormSubmit}
        >
          <div className="h3 form-title">Create a Restaurant</div>

          {/* Restaurant Name and Website (from ad hoc search) */}
          {(formData.name || formData.website) && (
            <div style={{ marginBottom: "24px", padding: "16px", backgroundColor: "#F5F5F5", borderRadius: "8px" }}>
              {formData.name && (
                <h4 style={{ margin: "0 0 8px 0", fontSize: "20px", fontWeight: "600" }}>
                  {formData.name}
                </h4>
              )}
              {formData.website && (
                <p style={{ margin: "0", fontSize: "14px", color: "#757575" }}>
                  <a
                    href={formData.website.startsWith("http") ? formData.website : `https://${formData.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#1976d2", textDecoration: "none" }}
                  >
                    {formData.website.replace(/^https?:\/\//, "").replace(/^www\./, "")}
                    <i className="fas fa-external-link-alt" style={{ marginLeft: "4px", fontSize: "0.8em" }}></i>
                  </a>
                </p>
              )}
            </div>
          )}

          {/* Summary Stats */}
          {predictedOptions && (
            <div style={{ marginBottom: "24px", display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ flex: 1, padding: "12px", backgroundColor: "#F5F5F5", borderRadius: "8px" }}>
                <div style={{ fontSize: "14px", color: "#757575" }}>
                  Total items: {categorization.totalItems}
                  {" \u2022 "}
                  Marked as vegan: {categorization.veganCount}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFailureReportModalOpen(true)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#9e9e9e",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: "4px 8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  transition: "background-color 0.2s, color 0.2s",
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(158, 158, 158, 0.1)";
                  e.currentTarget.style.color = "#757575";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#9e9e9e";
                }}
                title="Report processing failure"
                aria-label="Report processing failure"
              >
                <i className="fas fa-flag"></i>
              </button>
            </div>
          )}

          <div className="form-section form-section-wide">
            <label className="form-label">Menu*</label>

            {predictedOptions && (
              <MenuCategorizationPanel
                predictedOptions={predictedOptions}
                categorization={categorization}
                editable
                urlIndexMap={Object.keys(urlIndexMap).length > 0 ? urlIndexMap : undefined}
                veganLegend={formData.veganLegend}
                contributingUrls={formData.contributingUrls}
                fullMenuVeganSignal={formData.fullMenuVeganSignal}
                onDismissFullyVegan={formData.fullMenuVeganSignal?.detected ? async () => {
                  const items = (predictedOptions.GUARANTEED_VEGAN || [])
                    .filter((item) => item.source === "fully_vegan_restaurant")
                    .map((item) => ({
                      option: item.option,
                      description: item.description || "",
                      section: item.section,
                    }));

                  const res = await axios.post("/api/restaurant_builder/reclassify", { items });
                  const reclassified = res.data;

                  setFormData((prev) => ({
                    ...prev,
                    predictedOptions: {
                      GUARANTEED_VEGAN: (prev.predictedOptions.GUARANTEED_VEGAN || []).filter(
                        (item) => item.source !== "fully_vegan_restaurant"
                      ),
                      PROBABLY_VEGAN: [
                        ...(prev.predictedOptions.PROBABLY_VEGAN || []),
                        ...(reclassified.PROBABLY_VEGAN || []),
                      ],
                      MAYBE_VEGAN: [
                        ...(prev.predictedOptions.MAYBE_VEGAN || []),
                        ...(reclassified.MAYBE_VEGAN || []),
                      ],
                      PROBABLY_NOT_VEGAN: [
                        ...(prev.predictedOptions.PROBABLY_NOT_VEGAN || []),
                        ...(reclassified.PROBABLY_NOT_VEGAN || []),
                      ],
                    },
                    fullMenuVeganSignal: null,
                  }));
                } : undefined}
              />
            )}
          </div>

          {/* Cuisine picker */}
          <div className="form-section form-section-wide">
            <label className="form-label">Restaurant Cuisine*</label>
            {cuisineError && (
              <div style={{ color: "#d32f2f", fontSize: "14px", marginBottom: "8px" }}>
                {cuisineError}
              </div>
            )}
            <div className="form-flex">
              <CuisinePicker
                className="form-control noFlex"
                passedCuisines={cuisines}
                selectedCuisines={selectedCuisines}
                otherCuisineText={otherCuisineText}
                updateSelectedCuisines={updateSelectedCuisines}
                setOtherCuisineText={setOtherCuisineText}
              />
            </div>
          </div>

          <PanelContainer className="form-flex form-flex-center">
            <input
              className="btn-primary btn-center"
              style={{ height: "2em" }}
              type="submit"
              value={formData.geoJson && !formData.address ? "Create Restaurant" : "Continue to Location"}
              disabled={Object.keys(selectedCuisines).length === 0 && !otherCuisineText.trim()}
            />
            <Link
              to="/create/v2/cuisine/manual"
              state={{ ...location.state }}
            >
              Edit menu manually instead
            </Link>
          </PanelContainer>
        </form>
        {failureReportModalElement}
      </div>
    </div>
  );
};

export default connect(null, {
  updateRestaurant,
  updateLocation,
  setAlert,
})(RestaurantCuisine);
